/* eslint-disable unicode-bom */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { apiConfig } from '../config/api';
import CustomerHeader from '../components/CustomerHeader';

const OrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadOrders();
    }
  }, [isAuthenticated, user]);

  // Auto-refresh disabled - orders will only refresh when user clicks "My Orders"
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     loadOrders();
  //   }, 30000); // Refresh every 30 seconds

  //   return () => clearInterval(interval);
  // }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        setError('Please log in to view your orders');
        setLoading(false);
        return;
      }
      
      console.log('Loading orders for current user:', user.email, 'User ID:', user.id);
      
      // Use the apiService to get orders for the specific user
      const ordersData = await apiService.getUserOrders(user.id);
      console.log('Orders data received:', ordersData);
      
      setOrders(ordersData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'processing':
        return 'secondary';
      case 'shipped':
        return 'primary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏱';
      case 'confirmed':
        return '✓';
      case 'processing':
        return '⚙';
      case 'shipped':
        return '🚚';
      case 'delivered':
        return '📦';
      case 'cancelled':
        return '✗';
      default:
        return '?';
    }
  };

  const formatPrice = (price: number | undefined | null) => {
    if (price === undefined || price === null || isNaN(price)) {
      return '$0.00';
    }
    return `$${price.toFixed(2)}`;
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleTrackOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  if (loading) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate(-1)}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading your orders...
          </Typography>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <CustomerHeader
          variant="sticky"
          showTagline
          showVoiceSearch
          showProfileDropdown
          showBackButton
          onBackClick={() => navigate(-1)}
          onSearch={(query) => {
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          onCartClick={() => navigate('/cart')}
        />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={loadOrders}>
            Retry
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate(-1)}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          My Orders
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            onClick={loadOrders}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
          >
            Continue Shopping
          </Button>
        </Box>
      </Box>

      {lastUpdated && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Last updated: {lastUpdated.toLocaleTimeString()}
        </Typography>
      )}

      {orders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You haven't placed any orders yet.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
          >
            Start Shopping
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => (
            <Grid item xs={12} key={order.id}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Order #{order.order_number || order.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Placed on {new Date(order.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={order.status.toUpperCase()}
                        color={getStatusColor(order.status)}
                        variant="outlined"
                        icon={<span>{getStatusIcon(order.status)}</span>}
                      />
                      <Typography variant="h6" color="primary">
                        {formatPrice(order.total_amount)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Items Summary */}
                  <Typography variant="subtitle1" gutterBottom>
                    Items ({order.items?.length || 0})
                  </Typography>
                  <List dense>
                    {order.items?.slice(0, 3).map((item: any, index: number) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            📦
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.product_name || `Product ${item.product_id}`}
                          secondary={`Qty: ${item.quantity || 0} x ${formatPrice(item.unit_price)}`}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {formatPrice((item.quantity || 0) * (item.unit_price || 0))}
                        </Typography>
                      </ListItem>
                    ))}
                    {order.items?.length > 3 && (
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={`... and ${order.items.length - 3} more items`}
                          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                        />
                      </ListItem>
                    )}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  {/* Order Actions */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Payment: {order.payment_method || 'Not specified'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Delivery: {order.shipping_address_id || 'Address not specified'}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Order Details">
                        <IconButton
                          onClick={() => handleViewOrder(order.id)}
                          color="primary"
                        >
                          <span>👁</span>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Track Order">
                        <IconButton
                          onClick={() => handleTrackOrder(order.id)}
                          color="secondary"
                        >
                          <span>🚚</span>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Order Summary */}
      {orders.length > 0 && (
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">
              Total Orders: {orders.length}
            </Typography>
            <Typography variant="h6" color="primary">
              Total Spent: {formatPrice(orders.reduce((sum, order) => sum + (order.total_amount || 0), 0))}
            </Typography>
          </Box>
        </Paper>
      )}
      </Container>
    </>
  );
};

export default OrdersPage;
